'use client';
import React, { useState, useEffect } from 'react';
import { Star, Check, ChevronLeft, ChevronRight, Plane, Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Sarah Johnson',
    location: 'New York, USA',
    rating: 5,
    title: 'Exceeded all expectations!',
    text: "Faremaps found me a direct ticket to London at a wholesale rate I couldn't find anywhere else. The booking process was super smooth and the 24/7 dedicated support desk made sure my seating request was locked in. Absolutely brilliant service!",
    time: '2 weeks ago',
    initials: 'SJ',
    gradient: 'from-sky-400 via-blue-500 to-indigo-600',
    route: 'JFK ➔ LHR',
    carrier: 'British Airways',
    class: 'First Class',
    savings: '$620 Saved',
    categories: ['all', 'unpublished', 'business'],
  },
  {
    name: 'Michael Torres',
    location: 'Los Angeles, USA',
    rating: 5,
    title: 'Exceptional private rates',
    text: 'I saved over $300 on my round trip to Miami compared to other major portals. The travel agent on the phone was extremely knowledgeable, efficient, and walked me through multiple routing options. Outstanding service!',
    time: '1 month ago',
    initials: 'MT',
    gradient: 'from-blue-500 via-indigo-500 to-purple-600',
    route: 'LAX ➔ MIA',
    carrier: 'American Airlines',
    class: 'Economy Class',
    savings: '$310 Saved',
    categories: ['all', 'unpublished', 'support'],
  },
  {
    name: 'Jennifer Lee',
    location: 'Chicago, USA',
    rating: 5,
    title: 'First-class booking experience',
    text: 'Highly professional team. They assisted me with a last-minute business class booking for Paris, securing an incredible unpublished fare and premium seat allocation. Confirmation arrived instantly in my inbox.',
    time: '3 weeks ago',
    initials: 'JL',
    gradient: 'from-emerald-400 via-teal-500 to-cyan-600',
    route: 'ORD ➔ CDG',
    carrier: 'Air France',
    class: 'Business Class',
    savings: '$1,250 Saved',
    categories: ['all', 'business'],
  },
  {
    name: 'Robert Davis',
    location: 'Houston, USA',
    rating: 5,
    title: 'Phenomenal customer desk',
    text: 'The support specialist went above and beyond when I needed to adjust my flight dates due to a family emergency. He arranged a zero-change-fee solution within minutes. Their customer commitment is stellar!',
    time: '2 months ago',
    initials: 'RD',
    gradient: 'from-violet-500 via-purple-500 to-fuchsia-600',
    route: 'IAH ➔ CUN',
    carrier: 'United Airlines',
    class: 'Economy Class',
    savings: 'Free Change',
    categories: ['all', 'support'],
  },
  {
    name: 'Amanda Wilson',
    location: 'Miami, USA',
    rating: 5,
    title: 'Incredible elite discounts',
    text: 'I was skeptical about unpublished private fares at first, but Faremaps delivered on their best price guarantee. The live support representative got me booked quickly and easily. They are my ultimate go-to booking engine now.',
    time: '1 month ago',
    initials: 'AW',
    gradient: 'from-pink-500 via-rose-500 to-red-600',
    route: 'MIA ➔ LAS',
    carrier: 'Delta Air Lines',
    class: 'Premium Economy',
    savings: '$420 Saved',
    categories: ['all', 'unpublished'],
  },
  {
    name: 'Carlos Martinez',
    location: 'Dallas, USA',
    rating: 5,
    title: 'Outstanding personal touch',
    text: 'From start to finish, this was a highly personalized booking. The ticketing agent explained all details clearly, double-checked my frequent flyer number, and sent the official airline confirmation code instantly. Highly recommend!',
    time: '3 months ago',
    initials: 'CM',
    gradient: 'from-sky-500 via-accent to-blue-700',
    route: 'DFW ➔ HNL',
    carrier: 'Hawaiian Airlines',
    class: 'Business Class',
    savings: '$890 Saved',
    categories: ['all', 'support', 'business'],
  },
];

const categories = [
  { id: 'all', label: 'All Reviews' },
  { id: 'unpublished', label: 'Unpublished Deals' },
  { id: 'business', label: 'Business & First' },
  { id: 'support', label: 'Expert Desk' },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`w-3.5 h-3.5 ${
            s <= rating
              ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.4)]'
              : 'text-slate-200'
          }`}
        />
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [gaugeAnimated, setGaugeAnimated] = useState(false);
  const [mounted, setMounted] = useState(false);
  const itemsPerPage = 3;

  useEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => setGaugeAnimated(true), 150);
    return () => clearTimeout(timer);
  }, []);

  const filteredTestimonials = testimonials.filter((t) => t.categories.includes(activeCategory));

  const totalPages = Math.ceil(filteredTestimonials.length / itemsPerPage);

  const handleCategoryChange = (catId: string) => {
    setActiveCategory(catId);
    setCurrentPage(0);
  };

  const nextSlide = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const visibleItems = filteredTestimonials.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  return (
    <section className="relative bg-gradient-to-b from-slate-50 via-white to-slate-50 py-24 overflow-hidden border-t border-b border-slate-100">
      {/* Blueprint Grid Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.012)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.012)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" />

      {/* Decorative Glowing Orbs */}
      <div className="absolute top-[-100px] left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-accent/5 to-transparent rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-100px] right-1/4 w-[500px] h-[500px] bg-gradient-to-tr from-primary/5 to-transparent rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-[1170px] mx-auto px-6 relative z-10">
        {/* Groundbreaking Asymmetric Double-Column Header Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 items-stretch">
          {/* Left: Luxury Gold Trust Monolith Card */}
          <div className="lg:col-span-4 bg-slate-950 text-white rounded-[36px] p-8 border border-white/5 relative overflow-hidden shadow-2xl flex flex-col justify-between text-left group">
            {/* Ambient Background Gold Glow */}
            <div className="absolute -top-16 -right-16 w-36 h-36 bg-accent/20 rounded-full blur-2xl group-hover:scale-120 transition-transform duration-700" />

            <div>
              <div
                style={{ height: '30px' }}
                className="inline-flex items-center gap-1 bg-white/10 border border-white/10 px-3 py-1 rounded-full text-[9px] font-black text-sky-400 uppercase tracking-widest mb-6"
              >
                EXECUTIVE AUDIT
              </div>

              <div className="flex items-baseline gap-2">
                <span className="text-6xl font-black text-white tracking-tighter font-display leading-none">
                  98%
                </span>
                <span className="text-[10px] text-sky-400 font-extrabold uppercase tracking-wider">
                  SATISFACTION
                </span>
              </div>

              <div className="mt-3">
                <StarRating rating={5} />
              </div>

              <p className="text-slate-400 text-xs font-semibold leading-relaxed mt-4">
                Verified travel satisfaction index based on independent private client audits and
                secure airline contract deliveries.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10 flex flex-col gap-3">
              <div className="flex items-center gap-2.5 text-[10px] text-slate-300 font-extrabold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse"></span>
                <span>125,000+ Booked Flights</span>
              </div>
              <div className="flex items-center gap-2.5 text-[10px] text-slate-300 font-extrabold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span>4.8/5.0 verified score</span>
              </div>
            </div>
          </div>

          {/* Right: Editorial Main Title and Scorecard Panel */}
          <div className="lg:col-span-8 flex flex-col justify-between py-2 text-left">
            <div>
              <div
                style={{ height: '30px' }}
                className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/25 px-3.5 py-1 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-5 shadow-sm"
              >
                GLOBAL TRUST & CERTIFIED RATINGS
              </div>

              <h2 className="text-3xl lg:text-[42px] font-black text-slate-900 tracking-tight leading-none mb-4 uppercase font-display">
                98% Of Travelers Recommend Faremaps
              </h2>

              <p className="text-slate-500 text-sm md:text-base font-semibold leading-relaxed max-w-2xl">
                Direct connections to wholesale private flight inventories. Explore verified client
                testimonies, certified financial savings, and elite customer concierge experiences.
              </p>
            </div>

            {/* Google / Trustpilot Minimalist Scorecard Banner */}
            <div className="mt-8 flex flex-wrap items-center gap-6 bg-white border border-slate-200/60 p-5 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.03)] backdrop-blur-md self-start">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-accent text-white font-black text-lg shadow-[0_4px_14px_rgba(0,98,227,0.25)]">
                4.8
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <StarRating rating={5} />
                  <span className="text-[9px] font-black text-accent uppercase tracking-wider">
                    EXCELLENT
                  </span>
                </div>
                <div className="font-extrabold text-slate-800 text-xs mt-1.5">
                  Independent Flier Auditing Rating Index
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Tab Navigation Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-10 border-b border-slate-200/60 pb-6">
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-4 py-2 sm:px-6 sm:py-2.5 rounded-xl text-[10px] sm:text-xs font-bold transition-all duration-300 ${
                  activeCategory === cat.id
                    ? 'bg-slate-950 text-white shadow-md'
                    : 'bg-white border border-slate-200/60 text-slate-500 hover:text-slate-800 hover:bg-slate-100 hover:border-slate-300'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Manual Carousel Controls */}
          {mounted && totalPages > 1 && (
            <div className="flex gap-2 self-end sm:self-auto">
              <button
                onClick={prevSlide}
                className="w-10 h-10 rounded-xl bg-white border border-slate-200 hover:border-accent hover:bg-slate-950 hover:text-white text-slate-500 flex items-center justify-center transition-all duration-300 shadow-sm active:scale-95"
                aria-label="Previous Page"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextSlide}
                className="w-10 h-10 rounded-xl bg-white border border-slate-200 hover:border-accent hover:bg-slate-950 hover:text-white text-slate-500 flex items-center justify-center transition-all duration-300 shadow-sm active:scale-95"
                aria-label="Next Page"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Dynamic Styles for Staggered 3D Cascade */}
        <style>{`
          @keyframes cascadeIn {
            0% {
              transform: scale(0.88) translateY(60px) rotate(-3deg);
              opacity: 0;
              filter: blur(4px);
            }
            100% {
              transform: scale(1) translateY(0) rotate(0deg);
              opacity: 1;
              filter: blur(0px);
            }
          }
        `}</style>

        {/* Testimonial Cards Layout Grid */}
        <div
          key={`${activeCategory}-${currentPage}`}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {visibleItems.map((t, i) => (
            <div
              key={i}
              className="group flex bg-white border border-slate-100 hover:border-accent/25 shadow-[0_10px_35px_rgba(15,23,42,0.015)] hover:shadow-[0_25px_65px_rgba(234,88,12,0.06)] rounded-[36px] transition-all duration-500 hover:-translate-y-2 relative overflow-hidden select-none text-left"
              style={{
                minHeight: '310px',
                animation: `cascadeIn 800ms cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 60}ms both`,
              }}
            >
              {/* Semi-Circular Boarding Pass Ticket Notches */}
              <div className="absolute top-0 left-16 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-slate-50 border border-slate-200/80 shadow-[0_2px_4px_rgba(15,23,42,0.08)] z-20 pointer-events-none" />
              <div className="absolute bottom-0 left-16 -translate-x-1/2 translate-y-1/2 w-4 h-4 rounded-full bg-slate-50 border border-slate-200/80 shadow-[0_-2px_4px_rgba(15,23,42,0.08)] z-20 pointer-events-none" />

              {/* Left Column: Tactical Carbon Baggage Spine */}
              <div className="w-16 bg-slate-950 flex flex-col justify-between items-center py-6 px-2 text-white border-r-2 border-dashed border-white/20 flex-shrink-0 relative overflow-hidden">
                {/* Dashed Passport Track Background */}
                <div className="absolute left-1/2 -translate-x-1/2 top-16 bottom-16 border-l border-dashed border-white/10 pointer-events-none z-0" />

                {/* Tactical Gold Metal Grommet Eyelet */}
                <div className="w-4 h-4 rounded-full border-2 border-accent/40 bg-slate-950 flex items-center justify-center shadow-[inset_0_1.5px_3px_rgba(0,0,0,0.8)] mb-3 relative z-10">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-900 border border-black/40"></div>
                </div>

                {/* Gold Departure Code & Tiny Tag */}
                <div className="text-center relative z-10">
                  <div className="text-[10px] font-black text-accent-light tracking-wider font-mono uppercase">
                    {t.route.split('➔')[0].trim()}
                  </div>
                  <div className="text-[5.5px] font-black text-slate-500 uppercase tracking-widest leading-none mt-0.5">
                    DEP
                  </div>
                </div>

                {/* Luxury Vertical Rotated Seating Tier */}
                <div
                  className="text-[8.5px] font-black tracking-[0.3em] text-white/50 uppercase font-mono whitespace-nowrap my-auto select-none relative z-10 bg-slate-950 py-3"
                  style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                  }}
                >
                  {t.class}
                </div>

                {/* Gold Destination Code & Tiny Tag */}
                <div className="text-center relative z-10">
                  <div className="text-[5.5px] font-black text-slate-500 uppercase tracking-widest leading-none mb-0.5">
                    ARR
                  </div>
                  <div className="text-[10px] font-black text-accent-light tracking-wider font-mono uppercase">
                    {t.route.split('➔')[1].trim()}
                  </div>
                </div>
              </div>

              {/* Right Column: Luxury Porcelain Review Slab */}
              <div className="p-8 flex flex-col justify-between flex-grow bg-gradient-to-br from-white via-white to-slate-50/30 relative">
                {/* Soft editorial watermark quote */}
                <span className="absolute right-6 top-4 text-slate-100/60 text-7xl font-serif select-none pointer-events-none group-hover:text-accent/5 transition-colors duration-500">
                  “
                </span>

                <div>
                  {/* Header: User Profile Info */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-black text-slate-900 text-[15px] tracking-tight group-hover:text-accent transition-colors leading-none">
                        {t.name}
                      </div>
                      <div className="text-[9px] text-slate-400 font-extrabold uppercase tracking-widest mt-1.5">
                        {t.location}
                      </div>
                    </div>

                    <div className="text-right flex flex-col items-end">
                      <StarRating rating={t.rating} />
                      <span className="text-[7.5px] text-slate-400 font-black tracking-widest uppercase block mt-1.5">
                        {t.carrier}
                      </span>
                    </div>
                  </div>

                  {/* Editorial Quote Title */}
                  <h4 className="font-black text-slate-900 text-[15.5px] tracking-tight leading-snug mt-6 mb-2">
                    "{t.title}"
                  </h4>

                  {/* Quote Body Narrative */}
                  <p className="text-slate-500 text-[12px] font-semibold leading-relaxed line-clamp-4 italic">
                    {t.text}
                  </p>
                </div>

                {/* Footer details */}
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                  {/* Designer Boarding Barcode Strip */}
                  <div className="flex items-center gap-[2.5px] h-4.5 opacity-20 group-hover:opacity-35 transition-opacity duration-500 select-none pointer-events-none mx-4">
                    <span className="w-[1px] h-full bg-slate-900"></span>
                    <span className="w-[2px] h-full bg-slate-900"></span>
                    <span className="w-[1px] h-full bg-slate-900"></span>
                    <span className="w-[3.5px] h-full bg-slate-900"></span>
                    <span className="w-[1px] h-full bg-slate-900"></span>
                    <span className="w-[2px] h-full bg-slate-900"></span>
                    <span className="w-[1px] h-full bg-slate-900"></span>
                    <span className="w-[4.5px] h-full bg-slate-900"></span>
                    <span className="w-[1.5px] h-full bg-slate-900"></span>
                    <span className="w-[2.5px] h-full bg-slate-900"></span>
                  </div>

                  {/* Saving Metric Badge */}
                  <div
                    className={`text-[9.5px] font-black px-3.5 py-1.5 rounded-full border shadow-sm uppercase tracking-wider transition-all duration-300 group-hover:bg-slate-950 group-hover:text-white group-hover:border-slate-950 ${
                      t.savings.toLowerCase().includes('free')
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-500/20'
                        : 'bg-accent/5 text-accent border-accent/20'
                    }`}
                  >
                    {t.savings}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Elegant Pill-Shaped Pagination Dots */}
        {mounted && totalPages > 1 && (
          <div className="flex justify-center items-center gap-2.5 mt-10">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i)}
                className={`transition-all duration-500 rounded-full focus:outline-none ${
                  i === currentPage
                    ? 'w-7 h-2 bg-slate-950 shadow-[0_0_8px_rgba(15,23,42,0.15)]'
                    : 'w-2 h-2 bg-slate-200 hover:bg-slate-350'
                }`}
                aria-label={`Page ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
